import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GlobalContext from '../providers/GlobalContext'
import {
  IDLE_TIMEOUT_MS,
  extendSession,
  fetchSessionStatus,
  idleFor,
  markActivity,
} from '../helpers/session'

export const WARNING_THRESHOLD_MS = 30 * 1000
const POLL_INTERVAL_MS = 5 * 1000
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 1000
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'click', 'touchstart', 'wheel']

export default function useIdleLogout({ enabled = true } = {}) {
  const { logoutUser } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [warning, setWarning] = useState(null)
  const warnedRef = useRef(false)
  const expiringRef = useRef(false)

  const expire = useCallback(async () => {
    if (expiringRef.current) return
    expiringRef.current = true
    setWarning(null)
    await logoutUser()
    navigate('/login', { replace: true })
  }, [logoutUser, navigate])

  useEffect(() => {
    if (!enabled) return undefined
    const idleAtStart = idleFor()
    if (idleAtStart === null) {
      markActivity()
    } else if (idleAtStart >= IDLE_TIMEOUT_MS) {
      expire()
      return undefined
    }

    let lastWrite = 0
    const onActivity = () => {
      if (warnedRef.current) return
      const now = Date.now()
      if (now - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return
      lastWrite = now
      markActivity(now)
    }
    ACTIVITY_EVENTS.forEach((name) => document.addEventListener(name, onActivity, { passive: true }))

    let ticks = 0
    const poll = async () => {
      const idleMs = idleFor() ?? 0
      const remaining = IDLE_TIMEOUT_MS - idleMs
      if (remaining <= 0) {
        expire()
        return
      }
      if (remaining <= WARNING_THRESHOLD_MS) {
        if (!warnedRef.current) warnedRef.current = true
        setWarning(remaining)
        return
      }
      if (warnedRef.current) {
        warnedRef.current = false
        setWarning(null)
      }
      ticks += 1
      if (ticks % 12 === 0) {
        const status = await fetchSessionStatus()
        if (status?.authenticated === false) {
          const confirm = await fetchSessionStatus()
          if (confirm?.authenticated === false) expire()
        }
      }
    }
    const interval = setInterval(poll, POLL_INTERVAL_MS)

    return () => {
      clearInterval(interval)
      ACTIVITY_EVENTS.forEach((name) => document.removeEventListener(name, onActivity))
    }
  }, [enabled, expire])

  const staySignedIn = useCallback(async () => {
    const status = await extendSession()
    if (status?.authenticated) {
      warnedRef.current = false
      setWarning(null)
      return true
    }
    await expire()
    return false
  }, [expire])

  return { warningRemainingMs: warning, staySignedIn, signOut: expire }
}
